import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/lib/api-error';
import { FiscalSettingsCard } from '@/components/fiscal/FiscalSettingsCard';
import { LoanSettingsCard } from '@/components/loan/LoanSettingsCard';

interface HisobRequisites {
  name: string;
  legal_name: string;
  inn: string;
  bank_account: string;
  bank_code: string;
  bank_name: string;
  address: string;
  oked: string;
  vat_reg_code: string;
  director_name: string;
  director_pinfl: string;
  accountant_name: string;
  accountant_pinfl: string;
}

const emptyForm: HisobRequisites = {
  name: '', legal_name: '', inn: '', bank_account: '', bank_code: '', bank_name: '', address: '',
  oked: '', vat_reg_code: '', director_name: '', director_pinfl: '', accountant_name: '', accountant_pinfl: '',
};

/** Seed (standart) qiymatlar — bo'sh bo'lsa shu bilan to'ldiriladi */
const SEED: HisobRequisites = {
  name: 'Hisob MMT',
  legal_name: '"Hisob" mikromoliya tashkiloti MChJ',
  inn: '309876543',
  bank_account: '20208000900123456001',
  bank_code: '00443',
  bank_name: 'Ipoteka Bank ATIB',
  address: "Toshkent sh., Yunusobod tumani, Amir Temur ko'chasi 108",
  oked: '64920',
  vat_reg_code: '326090099999',
  director_name: 'Hisob Direktor',
  director_pinfl: '30101900000011',
  accountant_name: 'Hisob Buxgalter',
  accountant_pinfl: '30101900000022',
};

const FIELDS: { key: keyof HisobRequisites; label?: string; tKey?: string; mono?: boolean }[] = [
  { key: 'name', tKey: 'adm_settings.f_name' },
  { key: 'legal_name', tKey: 'adm_settings.f_legalName' },
  { key: 'inn', label: 'INN', mono: true },
  { key: 'bank_account', tKey: 'adm_settings.f_bankAccount', mono: true },
  { key: 'bank_code', tKey: 'adm_settings.f_bankCode', mono: true },
  { key: 'bank_name', tKey: 'adm_settings.f_bankName' },
  { key: 'address', tKey: 'adm_settings.f_address' },
  { key: 'oked', tKey: 'company_tax.oked', mono: true },
  { key: 'vat_reg_code', tKey: 'company_tax.vat_reg_code', mono: true },
  { key: 'director_name', tKey: 'hisob_requisites.director_name' },
  { key: 'director_pinfl', tKey: 'hisob_requisites.director_pinfl', mono: true },
  { key: 'accountant_name', tKey: 'hisob_requisites.accountant_name' },
  { key: 'accountant_pinfl', tKey: 'hisob_requisites.accountant_pinfl', mono: true },
];

const SETTING_KEY = 'hisob_requisites';

export default function AdminSettings() {
  const { t } = useTranslation();
  const { admin } = useAdminAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<HisobRequisites>(emptyForm);
  const [saving, setSaving] = useState(false);

  const canEdit = admin?.role === 'platform_owner' || admin?.role === 'finance_admin';

  const { data, isLoading: loading } = useQuery({
    queryKey: [`/admin/platform-settings/{key}`, { params: { path: { key: SETTING_KEY } } }],
  });

  useEffect(() => {
    if (data?.data) {
      const v = (data.data.value ?? {}) as Partial<HisobRequisites>;
      setForm({ ...emptyForm, ...v });
    }
  }, [data]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.inn.trim() || !form.bank_account.trim() || !form.bank_code.trim()) {
      toast.error(t('adm_settings.requiredFields'));
      return;
    }
    setSaving(true);
    try {
      const { error } = await apiClient.PUT('/admin/platform-settings/{key}', {
        params: { path: { key: SETTING_KEY } },
        body: { value: form },
      });
      if (error) throw error;
      toast.success(t('adm_settings.saved'));
      queryClient.invalidateQueries({ queryKey: [`/admin/platform-settings/{key}`] });
    } catch (err) {
      toast.error(apiErrorMessage(err, t('adm_settings.saveError')));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto animate-fade-in space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('adm_settings.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('adm_settings.subtitle')}
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('adm_settings.requisites')}</CardTitle>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setForm(SEED)} disabled={loading || saving}>
              {t('adm_settings.seedValues')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">{t('adm_common.loading')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(f => (
                <div key={f.key} className={f.key === 'address' || f.key === 'legal_name' ? 'md:col-span-2' : ''}>
                  <Label className="text-xs text-muted-foreground mb-1 block">{f.tKey ? t(f.tKey) : f.label}</Label>
                  <Input
                    value={form[f.key]}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    disabled={!canEdit}
                    className={f.mono ? 'font-mono' : undefined}
                  />
                </div>
              ))}
            </div>
          )}

          {canEdit ? (
            <Button className="mt-5" onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
              {saving ? t('adm_common.saving') : t('adm_common.save')}
            </Button>
          ) : (
            <p className="mt-5 text-xs text-muted-foreground">
              {t('adm_settings.editRestricted')}
            </p>
          )}
        </CardContent>
      </Card>

      <FiscalSettingsCard canEdit={admin?.role === 'platform_owner'} />

      <LoanSettingsCard canEdit={canEdit} />
    </div>
  );
}
